/**
 * <TimesheetSwimlane /> — daily calendar swimlane visualisation.
 *
 * Rows are people, columns are hours-of-day (default 07:00 → 21:00). Each
 * time entry is a colored block positioned by its start_time/end_time (or
 * a floating block sized by hours when unstamped).
 *
 * Toggle at top switches between "My day" (only me) and "Workspace" (every
 * member, admin only).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { apiErrorMessage } from "@/lib/apiError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverAnchor } from "@/components/ui/popover";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Calendar, ChevronLeft, ChevronRight, Users, UserRound, Copy, ChevronDown, Save, Trash2 } from "lucide-react";
import EmptyState from "@/components/EmptyState";
import EntryReviewButton from "@/components/timesheet/EntryReviewButton";
import { STATE_BLOCK, STATE_LABEL, STATE_ORDER } from "@/components/timesheet/stateColors";

const START_HOUR = 7;
const END_HOUR = 21;
const HOURS = END_HOUR - START_HOUR;

// Iter Feb2026 · Compact date formatter for the "On leave 09-11 → 09-12"
// chip on Daily Plan lane headers.
function shortDate(iso) {
  if (!iso) return "";
  const p = iso.split("-");
  return p.length === 3 ? `${p[1]}-${p[2]}` : iso;
}

const CATEGORY_COLORS = {
  social: "bg-pink-500/80 border-pink-600",
  seo: "bg-emerald-500/80 border-emerald-600",
  ads: "bg-amber-500/80 border-amber-600",
  content: "bg-sky-500/80 border-sky-600",
  web: "bg-indigo-500/80 border-indigo-600",
  email: "bg-purple-500/80 border-purple-600",
  meeting: "bg-teal-500/80 border-teal-600",
  documentation: "bg-orange-500/80 border-orange-600",
  adhoc: "bg-red-500/80 border-red-600",
  other: "bg-primary/80 border-primary",
};

// Stable, deterministic project colour palette — pick one of 10 hues
// based on a string hash of the project id / name.
const PROJECT_PALETTE = [
  "bg-sky-500/80 border-sky-600",
  "bg-emerald-500/80 border-emerald-600",
  "bg-amber-500/80 border-amber-600",
  "bg-pink-500/80 border-pink-600",
  "bg-indigo-500/80 border-indigo-600",
  "bg-purple-500/80 border-purple-600",
  "bg-teal-500/80 border-teal-600",
  "bg-orange-500/80 border-orange-600",
  "bg-red-500/80 border-red-600",
  "bg-lime-500/80 border-lime-600",
];

function projectColor(key) {
  const s = String(key || "other");
  let h = 0;
  for (let i = 0; i < s.length; i += 1) {
    h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  }
  return PROJECT_PALETTE[Math.abs(h) % PROJECT_PALETTE.length];
}

function parseHM(hm) {
  if (!hm || !/^\d{2}:\d{2}$/.test(hm)) return null;
  const [h, m] = hm.split(":").map(Number);
  return h + m / 60;
}

function fmtHM(decimalHours) {
  const clamped = Math.max(0, Math.min(24, decimalHours));
  const h = Math.floor(clamped);
  const m = Math.round((clamped - h) * 60);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

// snap a decimal-hour value to the nearest N-minute increment
function snapTo(v, minutes = 15) {
  const step = minutes / 60;
  return Math.round(v / step) * step;
}

// Legacy alias — kept as a thin wrapper for existing callers.
function snap15(v) { return snapTo(v, 15); }

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function shiftDate(iso, deltaDays) {
  const d = new Date(iso + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + deltaDays);
  return d.toISOString().slice(0, 10);
}

export default function TimesheetSwimlane() {
  const { activeWorkspaceId, user: currentUser } = useAuth();
  const [date, setDate] = useState(todayIso());
  const [scope, setScope] = useState("me"); // 'me' | 'workspace'
  const [colorBy, setColorBy] = useState("state"); // 'state' | 'category' | 'project'
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);
  const reload = () => setNonce((n) => n + 1);

  // Iter 125b — user-pref for keyboard nudge / snap increment (minutes).
  // Persisted per browser under acquilix.swimlane.nudgeMin.
  const [nudgeMin, setNudgeMin] = useState(() => {
    const raw = Number(localStorage.getItem("acquilix.swimlane.nudgeMin"));
    return [5, 10, 15, 30].includes(raw) ? raw : 15;
  });
  useEffect(() => { localStorage.setItem("acquilix.swimlane.nudgeMin", String(nudgeMin)); }, [nudgeMin]);

  // Iter 125b — templates state
  const [templates, setTemplates] = useState([]);
  const [saveTplOpen, setSaveTplOpen] = useState(false);

  const loadTemplates = async () => {
    if (!activeWorkspaceId) return;
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/timesheet/templates`);
      setTemplates(data || []);
    } catch { /* noop */ }
  };
  useEffect(() => { loadTemplates(); /* eslint-disable-next-line */ }, [activeWorkspaceId]);

  useEffect(() => {
    if (!activeWorkspaceId) return;
    let alive = true;
    setLoading(true);
    api.get(`/workspaces/${activeWorkspaceId}/timesheet/swimlane`, {
      params: { date, scope },
    })
      .then((r) => { if (alive) { setData(r.data); setLoading(false); } })
      .catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [activeWorkspaceId, date, scope, nonce]);

  // Feb 2026 R104 · Listen for `timesheet:changed` broadcasts (fired by
  // the Daily Plan popup after add / submit / approve) so blocks added
  // elsewhere immediately show up on this swimlane without a refresh.
  useEffect(() => {
    const onChanged = () => setNonce((n) => n + 1);
    window.addEventListener("timesheet:changed", onChanged);
    return () => window.removeEventListener("timesheet:changed", onChanged);
  }, []);

  const hourColumns = useMemo(() => Array.from({ length: HOURS }, (_, i) => START_HOUR + i), []);

  const canSeeWs = data?.can_see_workspace;

  const copyFromYesterday = async () => {
    try {
      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/timesheet/copy-from-previous?date=${date}`);
      if (data.copied > 0) {
        toast.success(`Copied ${data.copied} entr${data.copied === 1 ? "y" : "ies"} from ${data.source_date}${data.skipped?.length ? ` (${data.skipped.length} skipped due to overlap)` : ""}`);
      } else if (data.skipped?.length) {
        toast.info(`Nothing to copy — all ${data.skipped.length} entries would overlap existing ones`);
      } else {
        toast.info("No entries to copy");
      }
      reload();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Couldn't copy from previous day"));
    }
  };

  const copyFromLastWeek = async () => {
    try {
      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/timesheet/copy-from-week?date=${date}`);
      toast.success(`Copied ${data.copied} entries from same day last week (${data.source_date})${data.skipped?.length ? ` — ${data.skipped.length} skipped` : ""}`);
      reload();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Couldn't copy from last week"));
    }
  };

  const applyTemplate = async (tpl) => {
    try {
      const { data } = await api.post(`/timesheet-templates/${tpl.id}/apply?date=${date}`);
      toast.success(`Applied '${tpl.name}' — ${data.copied} entr${data.copied === 1 ? "y" : "ies"}${data.skipped?.length ? ` (${data.skipped.length} skipped)` : ""}`);
      reload();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Couldn't apply template"));
    }
  };

  const deleteTemplate = async (tpl) => {
    if (!window.confirm(`Delete template '${tpl.name}'?`)) return;
    try {
      await api.delete(`/timesheet-templates/${tpl.id}`);
      toast.success("Template deleted");
      loadTemplates();
    } catch (e) { toast.error(apiErrorMessage(e, "Couldn't delete")); }
  };

  return (
    <div className="space-y-4" data-testid="timesheet-swimlane">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" className="rounded-none h-9 w-9 p-0" onClick={() => setDate(shiftDate(date, -1))} data-testid="swimlane-prev-day"><ChevronLeft className="w-4 h-4" /></Button>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-9 rounded-none w-40" data-testid="swimlane-date" />
          <Button variant="outline" size="sm" className="rounded-none h-9 w-9 p-0" onClick={() => setDate(shiftDate(date, 1))} data-testid="swimlane-next-day"><ChevronRight className="w-4 h-4" /></Button>
          <Button variant="ghost" size="sm" className="rounded-none h-9" onClick={() => setDate(todayIso())} data-testid="swimlane-today"><Calendar className="w-4 h-4 mr-1.5" /> Today</Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="rounded-none h-9"
                data-testid="swimlane-copy-menu"
                title="Reuse yesterday, last week or a saved template"
              >
                <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy / templates <ChevronDown className="w-3 h-3 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-64 rounded-none" data-testid="swimlane-copy-menu-content">
              <DropdownMenuItem onSelect={copyFromYesterday} data-testid="swimlane-copy-yesterday">Copy from yesterday</DropdownMenuItem>
              <DropdownMenuItem onSelect={copyFromLastWeek} data-testid="swimlane-copy-last-week">Copy same day last week</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-[10px] uppercase tracking-widest">Templates</DropdownMenuLabel>
              <DropdownMenuItem onSelect={() => setSaveTplOpen(true)} data-testid="swimlane-save-template">
                <Save className="w-3.5 h-3.5 mr-1.5" /> Save today as template…
              </DropdownMenuItem>
              {templates.length === 0 && (
                <div className="px-2 py-1.5 text-[11px] text-muted-foreground italic">No templates yet</div>
              )}
              {templates.map((tpl) => (
                <div key={tpl.id} className="flex items-center hover:bg-muted/40" data-testid={`swimlane-template-row-${tpl.id}`}>
                  <button
                    className="flex-1 text-left px-2 py-1.5 text-xs truncate"
                    onClick={() => applyTemplate(tpl)}
                    data-testid={`swimlane-apply-template-${tpl.id}`}
                    title={`Apply '${tpl.name}' to ${date}`}
                  >
                    {tpl.name} <span className="text-[10px] text-muted-foreground">· {tpl.entries?.length || 0} lines</span>
                  </button>
                  <button
                    onClick={() => deleteTemplate(tpl)}
                    className="p-1.5 text-muted-foreground hover:text-red-600"
                    title="Delete template"
                    data-testid={`swimlane-delete-template-${tpl.id}`}
                  ><Trash2 className="w-3 h-3" /></button>
                </div>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="flex-1" />
        <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground" title="Snap / arrow-key nudge increment for time blocks">
          <Label htmlFor="swimlane-nudge" className="text-[10px] uppercase tracking-widest">Snap</Label>
          <select
            id="swimlane-nudge"
            value={nudgeMin}
            onChange={(e) => setNudgeMin(Number(e.target.value))}
            className="h-9 rounded-none border border-border bg-background text-xs px-1.5"
            data-testid="swimlane-nudge-min"
          >
            <option value={5}>5 min</option>
            <option value={10}>10 min</option>
            <option value={15}>15 min</option>
            <option value={30}>30 min</option>
          </select>
        </div>
        {canSeeWs && (
          <Tabs value={scope} onValueChange={setScope}>
            <TabsList className="rounded-none h-9">
              <TabsTrigger value="me" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="swimlane-scope-me">
                <UserRound className="w-3.5 h-3.5 mr-1.5" /> My day
              </TabsTrigger>
              <TabsTrigger value="workspace" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="swimlane-scope-workspace">
                <Users className="w-3.5 h-3.5 mr-1.5" /> Workspace
              </TabsTrigger>
            </TabsList>
          </Tabs>
        )}
        <Tabs value={colorBy} onValueChange={setColorBy}>
          <TabsList className="rounded-none h-9">
            <TabsTrigger value="state" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs" data-testid="swimlane-color-state">
              By state
            </TabsTrigger>
            <TabsTrigger value="category" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs" data-testid="swimlane-color-category">
              By category
            </TabsTrigger>
            <TabsTrigger value="project" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs" data-testid="swimlane-color-project">
              By project
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground py-6">Loading swimlane…</div>
      ) : !data || (data.lanes || []).length === 0 ? (
        <EmptyState
          title="No entries yet"
          description="Log time entries with start / end times to see them on the daily swimlane."
          icon={Calendar}
          testid="swimlane-empty"
        />
      ) : (
        <div className="border border-border overflow-x-auto" data-testid="swimlane-canvas">
          {/* header row */}
          <div className="grid" style={{ gridTemplateColumns: `160px repeat(${HOURS}, minmax(60px, 1fr))` }}>
            <div className="border-b border-border/70 bg-secondary/40 px-3 py-2 text-[10px] uppercase tracking-wider font-medium">Person</div>
            {hourColumns.map((h) => (
              <div key={h} className="border-b border-border/70 bg-secondary/40 px-2 py-2 text-[10px] uppercase tracking-wider font-medium text-right border-l border-border/40">
                {String(h).padStart(2, "0")}:00
              </div>
            ))}
          </div>
          {/* lanes */}
          {(data.lanes || []).map((lane) => (
            <SwimlaneRow
              key={lane.user_id}
              lane={lane}
              hours={hourColumns}
              colorBy={colorBy}
              editable={lane.user_id === currentUser?.id}
              nudgeMin={nudgeMin}
              onChanged={reload}
            />
          ))}
        </div>
      )}

      {saveTplOpen && (
        <SaveTemplateDialog
          date={date}
          workspaceId={activeWorkspaceId}
          onClose={() => setSaveTplOpen(false)}
          onSaved={() => { setSaveTplOpen(false); loadTemplates(); }}
        />
      )}

      {/* legend */}
      <div className="flex items-center gap-3 flex-wrap text-[11px] text-muted-foreground pt-1">
        {colorBy === "state"
          ? STATE_ORDER.map((s) => (
              <span key={s} className="inline-flex items-center gap-1" data-testid={`swimlane-legend-state-${s}`}>
                <span className={`w-2.5 h-2.5 ${STATE_BLOCK[s].bg}`} />
                <span>{STATE_LABEL[s]}</span>
              </span>
            ))
          : colorBy === "category"
          ? Object.keys(CATEGORY_COLORS).slice(0, 8).map((c) => (
              <span key={c} className="inline-flex items-center gap-1">
                <span className={`w-2.5 h-2.5 ${CATEGORY_COLORS[c].split(" ")[0]}`} />
                <span className="capitalize">{c}</span>
              </span>
            ))
          : (() => {
              const projects = new Map();
              (data?.lanes || []).forEach((lane) => (lane.entries || []).forEach((e) => {
                if (e.project_id) projects.set(e.project_id, e.project_name || "—");
              }));
              return Array.from(projects.entries()).slice(0, 12).map(([pid, pname]) => (
                <span key={pid} className="inline-flex items-center gap-1">
                  <span className={`w-2.5 h-2.5 ${projectColor(pid).split(" ")[0]}`} />
                  <span className="truncate max-w-[150px]">{pname}</span>
                </span>
              ));
            })()}
      </div>
      <div className="text-[10px] text-muted-foreground italic">
        Tip: click a block, then use ← → to nudge and ↑ ↓ to resize by {nudgeMin}-minute steps. Shift-click a block to hold it in focus.
      </div>
    </div>
  );
}

function SaveTemplateDialog({ date, workspaceId, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      await api.post(`/workspaces/${workspaceId}/timesheet/templates`, {
        name: name.trim(), source_date: date,
      });
      toast.success(`Saved template '${name}'`);
      onSaved();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Couldn't save"));
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm rounded-none" data-testid="swimlane-save-template-dialog">
        <DialogHeader>
          <DialogTitle>Save {date} as template</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Input
            autoFocus
            placeholder="e.g. Standard Monday"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="h-9 rounded-none"
            data-testid="swimlane-save-template-name"
          />
          <p className="text-[11px] text-muted-foreground">
            Snapshots every time entry currently on {date}. Applying it later will re-create those entries on any target day, respecting your no-overlap rule.
          </p>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="rounded-none" data-testid="swimlane-save-template-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy} className="rounded-none" data-testid="swimlane-save-template-confirm">
            {busy ? "Saving…" : "Save template"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SwimlaneRow({ lane, hours, colorBy = "category", editable = false, nudgeMin = 15, onChanged }) {
  const trackRef = useRef(null);
  return (
    <div
      className="grid border-b border-border/40 hover:bg-secondary/20"
      style={{ gridTemplateColumns: `160px repeat(${hours.length}, minmax(60px, 1fr))` }}
      data-testid={`swimlane-lane-${lane.user_id}`}
    >
      <div className="px-3 py-2 flex items-center gap-2 min-w-0">
        <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-[10px] font-bold shrink-0">
          {(lane.user_name || "?").charAt(0).toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-xs font-medium truncate">{lane.user_name}</div>
          <div className="text-[10px] text-muted-foreground">{lane.total_hours}h logged</div>
          {/* Iter Feb2026 · Leave / WFH chip on the lane header — mirrors
              the Silent Members panel so managers see "why no data" inline. */}
          {(lane.leaves || []).length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1" data-testid={`swimlane-leaves-${lane.user_id}`}>
              {(lane.leaves || []).slice(0, 3).map((lv, idx) => (
                <span
                  key={`${lv.from_date}-${idx}`}
                  className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-medium ${
                    lv.kind === "wfh"
                      ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                      : "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
                  }`}
                  title={lv.note || `${lv.leave_type} ${lv.from_date} → ${lv.to_date}`}
                >
                  {lv.kind === "wfh" ? "WFH" : (lv.half_day ? "½ leave" : "On leave")} · {shortDate(lv.from_date)}{lv.from_date !== lv.to_date ? `→${shortDate(lv.to_date)}` : ""}
                </span>
              ))}
              {(lane.leaves || []).length > 3 && (
                <span className="text-[9px] text-muted-foreground">+{lane.leaves.length - 3}</span>
              )}
            </div>
          )}
        </div>
      </div>
      <div ref={trackRef} className="relative" style={{ gridColumn: `2 / span ${hours.length}`, minHeight: "48px" }}>
        {/* hour grid backdrop */}
        <div className="absolute inset-0 grid pointer-events-none" style={{ gridTemplateColumns: `repeat(${hours.length}, 1fr)` }}>
          {hours.map((_, i) => (
            <div key={i} className="border-l border-border/30" />
          ))}
        </div>
        {(lane.entries || []).map((entry, i) => (
          <SwimlaneEntry
            key={entry.id}
            entry={entry}
            index={i}
            laneEntryCount={(lane.entries || []).length}
            trackRef={trackRef}
            colorBy={colorBy}
            editable={editable}
            nudgeMin={nudgeMin}
            onChanged={onChanged}
          />
        ))}
      </div>
    </div>
  );
}

// ───────────────  Interactive entry (drag + resize + inline edit)  ───────────────

const DRAG_NONE = { kind: null };

function SwimlaneEntry({ entry, index, laneEntryCount, trackRef, colorBy, editable, nudgeMin = 15, onChanged }) {
  const start = parseHM(entry.start_time);
  const end = parseHM(entry.end_time);
  const positioned = start != null && end != null && end > start;

  // Base geometry (in %) — kept in state so a live drag/resize doesn't need
  // to write to the server on every mousemove.
  const initial = (() => {
    if (positioned) {
      const leftPct = Math.max(0, ((start - START_HOUR) / HOURS) * 100);
      const widthPct = Math.min(100 - leftPct, ((end - start) / HOURS) * 100);
      return { leftPct, widthPct };
    }
    const perEntry = 100 / Math.max(1, laneEntryCount);
    return {
      leftPct: index * perEntry,
      widthPct: Math.max(3, Math.min(entry.hours / HOURS, 1) * 100 - 1),
    };
  })();

  const [geom, setGeom] = useState(initial);
  const [dragKind, setDragKind] = useState(DRAG_NONE.kind);
  const [popoverOpen, setPopoverOpen] = useState(false);
  // Iter 125b — Ghost preview during drag/resize. `origGeom` is the "before"
  // rectangle rendered as a dashed shadow; `geom` is the live destination.
  const [origGeom, setOrigGeom] = useState(null);

  // Sync geom when server value changes for THIS entry (e.g. reload after
  // another user edited). Skip during active drag to avoid stutter.
  useEffect(() => {
    if (dragKind) return;
    if (positioned) {
      const leftPct = Math.max(0, ((parseHM(entry.start_time) - START_HOUR) / HOURS) * 100);
      const widthPct = Math.min(100 - leftPct, ((parseHM(entry.end_time) - parseHM(entry.start_time)) / HOURS) * 100);
      setGeom({ leftPct, widthPct });
    }
  }, [entry.id, entry.start_time, entry.end_time, positioned, dragKind]);

  const color = colorBy === "state"
    ? (() => {
        const s = STATE_BLOCK[entry.state] || STATE_BLOCK.submitted;
        return `${s.bg} ${s.border}`;
      })()
    : colorBy === "project"
    ? projectColor(entry.project_id || entry.project_name)
    : (CATEGORY_COLORS[entry.category] || CATEGORY_COLORS.other);

  // ── drag / resize handlers
  const beginDrag = (kind) => (e) => {
    if (!editable || !positioned) return;
    e.stopPropagation();
    e.preventDefault();
    const track = trackRef.current;
    if (!track) return;
    const rect = track.getBoundingClientRect();
    const pxPerHour = rect.width / HOURS;
    const originX = e.clientX;
    const orig = { ...geom };
    setOrigGeom(orig);   // ghost outline of the ORIGINAL position
    setDragKind(kind);
    const move = (ev) => {
      const dx = ev.clientX - originX;
      const dHours = dx / pxPerHour;
      if (kind === "move") {
        let newLeftHours = ((orig.leftPct / 100) * HOURS) + dHours;
        newLeftHours = snapTo(newLeftHours, nudgeMin);
        const durationHours = (orig.widthPct / 100) * HOURS;
        newLeftHours = Math.max(0, Math.min(HOURS - durationHours, newLeftHours));
        setGeom({
          leftPct: (newLeftHours / HOURS) * 100,
          widthPct: (durationHours / HOURS) * 100,
        });
      } else if (kind === "resize-left") {
        let newLeftHours = ((orig.leftPct / 100) * HOURS) + dHours;
        newLeftHours = snapTo(newLeftHours, nudgeMin);
        const rightHours = ((orig.leftPct + orig.widthPct) / 100) * HOURS;
        newLeftHours = Math.max(0, Math.min(rightHours - nudgeMin / 60, newLeftHours));
        setGeom({
          leftPct: (newLeftHours / HOURS) * 100,
          widthPct: ((rightHours - newLeftHours) / HOURS) * 100,
        });
      } else if (kind === "resize-right") {
        let newDurHours = (orig.widthPct / 100) * HOURS + dHours;
        newDurHours = snapTo(newDurHours, nudgeMin);
        newDurHours = Math.max(nudgeMin / 60, Math.min(HOURS - ((orig.leftPct / 100) * HOURS), newDurHours));
        setGeom({
          leftPct: orig.leftPct,
          widthPct: (newDurHours / HOURS) * 100,
        });
      }
    };
    const up = async () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      setDragKind(null);
      setOrigGeom(null);
      // Persist
      const startHours = snapTo((geomRef.current.leftPct / 100) * HOURS + START_HOUR, nudgeMin);
      const endHours = snapTo(startHours + (geomRef.current.widthPct / 100) * HOURS, nudgeMin);
      const newStart = fmtHM(startHours);
      const newEnd = fmtHM(endHours);
      if (newStart === entry.start_time && newEnd === entry.end_time) return;
      try {
        await api.patch(`/time-entries/${entry.id}`, {
          start_time: newStart,
          end_time: newEnd,
          hours: Math.round((endHours - startHours) * 4) / 4,
        });
        onChanged?.();
      } catch (err) {
        toast.error(apiErrorMessage(err, "Couldn't update entry"));
        setGeom(orig); // revert
      }
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };
  // keep a ref of the latest geom so mouseup can read final value w/o closure staleness
  const geomRef = useRef(geom);
  useEffect(() => { geomRef.current = geom; }, [geom]);

  // Iter 125b — Keyboard nudges. When the block has focus and no popover
  // is open, arrow keys shift/resize by nudgeMin minutes. Modifiers:
  //   ← / →  move by nudge step (Shift = 4× step)
  //   ↑ / ↓  grow / shrink from RIGHT edge (Shift = 4× step)
  //   Home / End  jump start to top-of-hour / end-of-hour
  const onKeyDown = async (e) => {
    if (!editable || !positioned || popoverOpen || dragKind) return;
    const step = (nudgeMin / 60) * (e.shiftKey ? 4 : 1);
    let handled = true;
    let nextLeft = (geomRef.current.leftPct / 100) * HOURS + START_HOUR;
    let nextEnd = nextLeft + (geomRef.current.widthPct / 100) * HOURS;
    switch (e.key) {
      case "ArrowLeft": nextLeft -= step; nextEnd -= step; break;
      case "ArrowRight": nextLeft += step; nextEnd += step; break;
      case "ArrowUp": nextEnd += step; break;
      case "ArrowDown": nextEnd -= step; break;
      default: handled = false;
    }
    if (!handled) return;
    e.preventDefault();
    e.stopPropagation();
    if (nextLeft < START_HOUR) { const shift = START_HOUR - nextLeft; nextLeft += shift; nextEnd += shift; }
    if (nextEnd > END_HOUR) { const shift = nextEnd - END_HOUR; nextLeft -= shift; nextEnd -= shift; }
    if (nextEnd - nextLeft < nudgeMin / 60) nextEnd = nextLeft + nudgeMin / 60;
    const newStart = fmtHM(snapTo(nextLeft, nudgeMin));
    const newEnd = fmtHM(snapTo(nextEnd, nudgeMin));
    if (newStart === entry.start_time && newEnd === entry.end_time) return;
    // Optimistic UI update
    setGeom({
      leftPct: ((snapTo(nextLeft, nudgeMin) - START_HOUR) / HOURS) * 100,
      widthPct: ((snapTo(nextEnd, nudgeMin) - snapTo(nextLeft, nudgeMin)) / HOURS) * 100,
    });
    try {
      await api.patch(`/time-entries/${entry.id}`, {
        start_time: newStart, end_time: newEnd,
        hours: Math.round((snapTo(nextEnd, nudgeMin) - snapTo(nextLeft, nudgeMin)) * 4) / 4,
      });
      onChanged?.();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Couldn't nudge entry"));
      // Revert on error
      const l = ((parseHM(entry.start_time) - START_HOUR) / HOURS) * 100;
      const w = ((parseHM(entry.end_time) - parseHM(entry.start_time)) / HOURS) * 100;
      setGeom({ leftPct: l, widthPct: w });
    }
  };

  const startLabel = positioned ? fmtHM((geom.leftPct / 100) * HOURS + START_HOUR) : (entry.start_time || "—");
  const endLabel = positioned ? fmtHM(((geom.leftPct + geom.widthPct) / 100) * HOURS + START_HOUR) : (entry.end_time || "—");
  const durHours = positioned ? Math.round(((geom.widthPct / 100) * HOURS) * 4) / 4 : entry.hours;

  return (
    <Popover open={popoverOpen} onOpenChange={(v) => { if (dragKind) return; setPopoverOpen(v); }}>
      <PopoverAnchor asChild>
        <div
          className={`absolute top-1 bottom-1 border ${color} flex items-center overflow-hidden text-[10px] text-white shadow-sm select-none focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 ${positioned ? "" : "opacity-70"} ${dragKind === "move" ? "cursor-grabbing" : editable && positioned ? "cursor-grab" : "cursor-pointer"} ${dragKind ? "brightness-110 ring-2 ring-primary/60" : "hover:brightness-110"} transition`}
          style={{ left: `${geom.leftPct}%`, width: `calc(${geom.widthPct}% - 2px)` }}
          title={`${entry.task_title || "Untitled"} · ${entry.project_name || ""} · ${durHours}h (${startLabel}–${endLabel})${entry.note ? `\n${entry.note}` : ""}\nClick then use arrow keys to nudge by ${nudgeMin}m (Shift = ${4 * nudgeMin}m)`}
          data-testid={`swimlane-entry-${entry.id}`}
          onMouseDown={beginDrag("move")}
          onClick={(e) => {
            if (dragKind) return;
            e.stopPropagation();
            if (editable) setPopoverOpen(true);
          }}
          tabIndex={editable && positioned ? 0 : -1}
          onKeyDown={onKeyDown}
        >
          {editable && positioned && (
            <span
              className="absolute left-0 top-0 bottom-0 w-1.5 cursor-ew-resize hover:bg-white/50"
              onMouseDown={beginDrag("resize-left")}
              data-testid={`swimlane-entry-resize-left-${entry.id}`}
              onClick={(e) => e.stopPropagation()}
            />
          )}
          <span className="px-1.5 truncate font-medium">{entry.task_title || entry.project_name || "Entry"}</span>
          {entry.rating > 0 && (
            <span className="px-1 text-amber-200 font-mono shrink-0"
                  title={entry.review_comment || `Rated ${entry.rating}/5`}
                  data-testid={`swimlane-entry-rating-${entry.id}`}>
              {"★".repeat(entry.rating)}
            </span>
          )}
          <span className="ml-auto pr-1.5 pl-1 font-mono opacity-90 shrink-0">{durHours}h</span>
          {editable && positioned && (
            <span
              className="absolute right-0 top-0 bottom-0 w-1.5 cursor-ew-resize hover:bg-white/50"
              onMouseDown={beginDrag("resize-right")}
              data-testid={`swimlane-entry-resize-right-${entry.id}`}
              onClick={(e) => e.stopPropagation()}
            />
          )}
        </div>
      </PopoverAnchor>
      {/* Ghost preview — dashed outline at the ORIGINAL rectangle, plus a floating time label at the destination while dragging */}
      {dragKind && origGeom && (
        <>
          <div
            aria-hidden
            className="absolute top-1 bottom-1 border-2 border-dashed border-primary/70 bg-primary/5 pointer-events-none"
            style={{ left: `${origGeom.leftPct}%`, width: `calc(${origGeom.widthPct}% - 2px)` }}
            data-testid={`swimlane-entry-ghost-${entry.id}`}
          />
          <div
            aria-hidden
            className="absolute -top-4 text-[10px] font-mono font-bold text-primary bg-background border border-primary px-1 py-0.5 pointer-events-none shadow"
            style={{ left: `${geom.leftPct}%` }}
            data-testid={`swimlane-entry-time-preview-${entry.id}`}
          >
            {startLabel}–{endLabel} · {durHours}h
          </div>
        </>
      )}
      <PopoverContent side="top" align="start" className="w-72 p-3 rounded-none" data-testid={`swimlane-entry-edit-${entry.id}`}>
        <EntryQuickEditor
          entry={entry}
          startLabel={startLabel}
          endLabel={endLabel}
          durHours={durHours}
          onClose={() => setPopoverOpen(false)}
          onChanged={() => { setPopoverOpen(false); onChanged?.(); }}
        />
      </PopoverContent>
    </Popover>
  );
}

function EntryQuickEditor({ entry, startLabel, endLabel, durHours, onClose, onChanged }) {
  const [note, setNote] = useState(entry.note || "");
  const [busy, setBusy] = useState(false);
  const [startVal, setStartVal] = useState(startLabel === "—" ? (entry.start_time || "") : startLabel);
  const [endVal, setEndVal] = useState(endLabel === "—" ? (entry.end_time || "") : endLabel);

  const save = async () => {
    setBusy(true);
    try {
      const patch = { note: note || null };
      if (startVal) patch.start_time = startVal;
      if (endVal) patch.end_time = endVal;
      if (startVal && endVal) {
        const [sh, sm] = startVal.split(":").map(Number);
        const [eh, em] = endVal.split(":").map(Number);
        const delta = (eh + em / 60) - (sh + sm / 60);
        if (delta > 0) patch.hours = Math.round(delta * 4) / 4;
      }
      await api.patch(`/time-entries/${entry.id}`, patch);
      toast.success("Saved");
      onChanged();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Couldn't save"));
    } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete entry '${entry.task_title || "(untitled)"}'?`)) return;
    setBusy(true);
    try {
      await api.delete(`/time-entries/${entry.id}`);
      toast.success("Entry deleted");
      onChanged();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Couldn't delete"));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-2.5" data-testid="entry-quick-editor">
      <div>
        <div className="text-xs font-semibold truncate">{entry.task_title || "Untitled entry"}</div>
        {entry.project_name && <div className="text-[10px] text-muted-foreground truncate">{entry.project_name}</div>}
      </div>
      <div className="flex items-center gap-2">
        <Input type="time" value={startVal} onChange={(e) => setStartVal(e.target.value)} className="h-8 w-24 rounded-none text-xs font-mono" data-testid="entry-edit-start" />
        <span className="text-xs text-muted-foreground">→</span>
        <Input type="time" value={endVal} onChange={(e) => setEndVal(e.target.value)} className="h-8 w-24 rounded-none text-xs font-mono" data-testid="entry-edit-end" />
        <span className="ml-auto text-xs font-mono font-bold">{durHours}h</span>
      </div>
      <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note" className="h-8 rounded-none text-xs" data-testid="entry-edit-note" />
      {/* R64 · Rating + comment surface on the swimlane popover — for
           submitted/approved entries. Shows current stars if reviewed.
           Uses `state` (R63 state-machine), not `status`. Legacy pre-R63
           rows have state=null which the backend treats as submitted. */}
      {(!entry.state || entry.state === "submitted" || entry.state === "approved") && (
        <div className="flex items-center gap-2 border-t border-border/40 pt-2">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Review</span>
          <EntryReviewButton entry={entry} onReviewed={onChanged} />
        </div>
      )}
      <div className="flex items-center gap-2 justify-end pt-1 border-t border-border/40">
        <Button variant="ghost" size="sm" className="rounded-none h-7 text-[11px] text-red-600 mr-auto" onClick={remove} disabled={busy} data-testid="entry-edit-delete">Delete</Button>
        <Button variant="ghost" size="sm" className="rounded-none h-7 text-[11px]" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button size="sm" className="rounded-none h-7 text-[11px]" onClick={save} disabled={busy} data-testid="entry-edit-save">Save</Button>
      </div>
    </div>
  );
}
